import React, { useCallback, useMemo, useState, Suspense, lazy } from 'react';
import styled, { useTheme } from 'styled-components';
import { useParams } from 'react-router-dom';
import { Loader2, Lock } from 'lucide-react';
import { accessSharedWorkspace, type SharedWorkspaceAccessResponse } from '@auw/api-client';
import { modeToPath } from '@auw/workspace-utils';
import type { ChatMessage, WorkspaceMessage } from '@auw/workspace-utils';
import { Avatar } from '@auw/workspace-ui';
import { MessageSkeleton } from '@auw/workspace-ui';
import { useFeature } from '@auw/feature-flags';
import {
  activeTenantAppName,
  resolveAppNameLogo,
  useTenant,
} from '@auw/tenant';

const MessageBubble = lazy(() =>
  import('@auw/workspace-ui').then((m) => ({ default: m.MessageBubble })),
);

const MarkdownRenderer = lazy(() =>
  import('@auw/workspace-ui').then((m) => ({ default: m.MarkdownRenderer })),
);

/* ------------------------------------------------------------------ */
/* WorkspaceMessage → ChatMessage 변환                                  */
/* ------------------------------------------------------------------ */

function toShareChatMessage(msg: WorkspaceMessage): ChatMessage {
  const metadata = (msg.metadata ?? {}) as Record<string, unknown>;
  return {
    id: msg.message_id,
    role: msg.role === 'assistant' ? 'assistant' : msg.role === 'system' ? 'system' : 'user',
    content: msg.content,
    createdAt: new Date(msg.created_at).getTime(),
    isStreaming: false,
    streamEvents: [],
    completedData: null,
    senderId: (metadata.senderId as string) ?? null,
    senderName: (metadata.senderName as string) ?? null,
  };
}

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

export const SharedWorkspacePage: React.FC = () => {
  const params = useParams<{ token?: string }>();
  const token = useMemo(() => {
    if (params.token) return params.token;
    const path = window.location.pathname;
    const parts = path.split('/').filter(Boolean);
    return parts.length >= 2 ? parts[1] : '';
  }, [params.token]);

  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(true);
  const [requiresPassword, setRequiresPassword] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState<SharedWorkspaceAccessResponse | null>(null);
  const theme = useTheme();
  const tenant = useTenant();
  /* Theme-aware wordmark. Returns `null` when the tenant has not
   * supplied a wordmark (or only a light variant in dark mode), in
   * which case the share page header omits the logo entirely instead
   * of rendering a low-contrast variant. Tenants that need their
   * wordmark visible across both themes declare both `appNameLogo`
   * and `appNameLogoDark` in the manifest. */
  const appNameLogo = resolveAppNameLogo(
    tenant,
    theme.mode === 'dark' ? 'dark' : 'light',
  );
  /* P11-5a: tenant policy gates. */
  const sharePublicViewEnabled = useFeature('share.publicView');
  const shareSignupCtaEnabled = useFeature('share.signupCta');
  const productName = activeTenantAppName();

  const loadSharedData = useCallback(async (pwd?: string) => {
    if (!token) {
      setLoading(false);
      setError('잘못된 공유 링크입니다.');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const result = await accessSharedWorkspace(token, pwd);
      setData(result);
      setRequiresPassword(false);
    } catch (err) {
      const message = (err as Error).message ?? '';
      if (message.includes('HTTP 401') || message.includes('비밀번호가 필요하거나 올바르지 않습니다.')) {
        setRequiresPassword(true);
        setError('비밀번호를 입력해 주세요.');
      } else if (message.includes('HTTP 404') || message.includes('공유 링크를 찾을 수 없습니다.')) {
        setError('공유 링크가 만료되었거나 존재하지 않습니다.');
      } else {
        setError('공유 데이터를 불러오지 못했습니다.');
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  React.useEffect(() => {
    void loadSharedData();
  }, [loadSharedData]);

  const chatMessages = useMemo(
    () => (data?.messages ?? []).map(toShareChatMessage),
    [data?.messages],
  );
  const sharedHeader = (
    <Header>
      <HeaderLeft>
        {appNameLogo && <LogoImg src={appNameLogo.src} alt={productName} />}
      </HeaderLeft>
      <HeaderRight>
        {shareSignupCtaEnabled && (
          <CTAButton href={modeToPath('agent')}>{`${productName} 시작하기`}</CTAButton>
        )}
      </HeaderRight>
    </Header>
  );

  /* P11-5a: when the tenant has disabled share.publicView, treat the
   * route as a soft 404. We still render the chrome so the link doesn't
   * leak whether the share token exists. */
  if (!sharePublicViewEnabled) {
    return (
      <PageWrap>
        {sharedHeader}
        <CenterState>
          <StateTitle>공유가 비활성화된 워크스페이스입니다</StateTitle>
          <StateText>이 테넌트는 외부 공개 공유를 허용하지 않습니다.</StateText>
        </CenterState>
      </PageWrap>
    );
  }

  /* ---- 로딩 ---- */
  if (loading) {
    return (
      <PageWrap>
        {sharedHeader}
        <CenterState>
          <Spinner><Loader2 size={28} /></Spinner>
          <StateText>공유 페이지를 불러오는 중...</StateText>
        </CenterState>
      </PageWrap>
    );
  }

  /* ---- 비밀번호 입력 ---- */
  if (!data && requiresPassword) {
    return (
      <PageWrap>
        {sharedHeader}
        <CenterState>
          <LockIcon><Lock size={32} /></LockIcon>
          <StateTitle>비밀번호가 필요한 공유 링크입니다</StateTitle>
          <PasswordForm onSubmit={(e) => { e.preventDefault(); void loadSharedData(password); }}>
            <PasswordInput
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="비밀번호 입력"
              autoFocus
            />
            <SubmitButton type="submit">확인</SubmitButton>
          </PasswordForm>
          {error && <ErrorText>{error}</ErrorText>}
        </CenterState>
      </PageWrap>
    );
  }

  /* ---- 에러 ---- */
  if (!data) {
    return (
      <PageWrap>
        {sharedHeader}
        <CenterState>
          <StateText>{error || '공유 데이터를 찾을 수 없습니다.'}</StateText>
        </CenterState>
      </PageWrap>
    );
  }

  /* ---- 메시지 or 콘텐츠 ---- */
  const contentPreview =
    data.content && data.content.content != null
      ? (typeof data.content.content === 'string'
        ? data.content.content
        : JSON.stringify(data.content.content, null, 2))
      : '';

  return (
    <PageWrap>
      {sharedHeader}

      <ChatBody>
        {chatMessages.length > 0 ? (
          <Suspense
            fallback={
              <MessageLoadingWrap>
                <MessageSkeleton />
                <MessageSkeleton />
                <MessageSkeleton />
              </MessageLoadingWrap>
            }
          >
            {chatMessages.map((msg) => (
              <MessageBubble
                key={msg.id}
                message={msg}
                sharedGroup={null}
                currentUserId=""
                conversationId=""
              />
            ))}
          </Suspense>
        ) : contentPreview ? (
          <ContentPreviewSection>
            <ContentPreviewHeader>
              <Avatar role="assistant" size={32} />
              <ContentPreviewLabel>공유된 페이지 콘텐츠</ContentPreviewLabel>
            </ContentPreviewHeader>
            <ContentPreviewBody>
              <Suspense fallback={<ContentPlainText>{contentPreview}</ContentPlainText>}>
                <MarkdownRenderer content={contentPreview} />
              </Suspense>
            </ContentPreviewBody>
          </ContentPreviewSection>
        ) : (
          <EmptyState>공유된 대화 메시지가 없습니다.</EmptyState>
        )}
      </ChatBody>
    </PageWrap>
  );
};

/* ------------------------------------------------------------------ */
/* Styles                                                              */
/* ------------------------------------------------------------------ */

const PageWrap = styled.div`
  height: 100vh;
  width: 100%;
  max-width: 768px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.colors.bgPrimary};
`;

const Header = styled.header`
  position: sticky;
  top: 0;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 56px;
  width: 100%;
  padding: 0 16px;
  background: ${({ theme }) => theme.colors.bgPrimary};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
  backdrop-filter: blur(12px);
  flex-shrink: 0;

  @media (max-width: 640px) {
    height: 50px;
    padding: 0 16px;
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const LogoImg = styled.img`
  height: 22px;
  width: auto;
  object-fit: contain;
  user-select: none;

  @media (max-width: 640px) {
    height: 20px;
  }
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
`;

const CTAButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 12px;
  height: 32px;
  border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};
  border: none;
  font-size: 0.8125rem;
  font-weight: 600;
  background: ${({ theme }) => theme.colors.textPrimary};
  color: ${({ theme }) => theme.colors.bgPrimary};
  text-decoration: none;
  white-space: nowrap;
  transition: opacity ${({ theme }) => theme.transitions.fast};
  cursor: pointer;

  &:hover {
    opacity: 0.85;
  }

  &:active {
    opacity: 0.78;
  }

  @media (max-width: 640px) {
    height: 30px;
    font-size: 0.78rem;
  }
`;

/* ---- Chat body ---- */

const ChatBody = styled.div`
  flex: 1;
  overflow-y: auto;
  overscroll-behavior-y: contain;
  -webkit-overflow-scrolling: touch;
  padding: 16px 0 48px;

  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar { display: none; }
`;

const MessageLoadingWrap = styled.div`
  padding: 0 16px;
  max-width: 768px;
  margin: 0 auto;
`;

/* ---- Content preview (non-message) ---- */

const ContentPreviewSection = styled.div`
  max-width: 768px;
  margin: 0 auto;
  padding: 0 16px;
`;

const ContentPreviewHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
`;

const ContentPreviewLabel = styled.span`
  font-size: 0.82rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const ContentPreviewBody = styled.div`
  font-size: 0.9rem;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const ContentPlainText = styled.pre`
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
  font-family: inherit;
  font-size: 0.9rem;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

/* ---- Center states (loading, error, password) ---- */

const CenterState = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  padding: 32px 20px;
`;

const Spinner = styled.div`
  color: ${({ theme }) => theme.colors.textMuted};
  animation: spin 1s linear infinite;

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const LockIcon = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 4px;
`;

const StateTitle = styled.h2`
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
  text-align: center;
`;

const StateText = styled.div`
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  text-align: center;
`;

const PasswordForm = styled.form`
  display: flex;
  gap: 8px;
  width: 100%;
  max-width: 340px;
`;

const PasswordInput = styled.input`
  flex: 1;
  padding: 10px 14px;
  border-radius: 10px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.bgSecondary};
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: 0.88rem;
  outline: none;
  transition: border-color 0.15s ease;

  &:focus {
    border-color: ${({ theme }) => theme.colors.accent};
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.textMuted};
  }
`;

const SubmitButton = styled.button`
  padding: 10px 20px;
  border-radius: 10px;
  border: none;
  background: ${({ theme }) => theme.colors.accent};
  color: #fff;
  font-size: 0.88rem;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.15s ease;
  white-space: nowrap;

  &:hover {
    opacity: 0.9;
  }
`;

const ErrorText = styled.div`
  font-size: 0.8rem;
  color: ${({ theme }) => theme.colors.error};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 0.9rem;
`;
